import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CurationCandidate } from '../api/types';
import ReelNameField from './ReelNameField';
import { isReelNameValid, normalizeReelName } from '../utils/reelName';

type CurationReviewPanelProps = {
  candidates: CurationCandidate[];
  deadlineAt?: string | null;
  submitting?: boolean;
  error?: string | null;
  initialReelName?: string;
  onSubmit: (selectedIds: string[], reelName: string) => void;
  /** Ephemeral signed source URL from GET …/candidates. Do not persist. */
  sourcePreviewUrl?: string | null;
  sourcePreviewExpiresAt?: string | null;
  onRefreshPreview?: () => void | Promise<void>;
  refreshingPreview?: boolean;
};

function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}

function isPreviewExpired(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return false;
  return Date.now() >= ms;
}

const CurationReviewPanel: React.FC<CurationReviewPanelProps> = ({
  candidates,
  deadlineAt,
  submitting = false,
  error = null,
  initialReelName = '',
  onSubmit,
  sourcePreviewUrl = null,
  sourcePreviewExpiresAt = null,
  onRefreshPreview,
  refreshingPreview = false,
}) => {
  const initialOrder = useMemo(
    () =>
      [...candidates].sort((a, b) => {
        if (a.selected_by_default !== b.selected_by_default) {
          return a.selected_by_default ? -1 : 1;
        }
        return (b.spectacle_score || 0) - (a.spectacle_score || 0);
      }),
    [candidates]
  );
  const [order, setOrder] = useState(() => initialOrder.map((c) => c.candidate_id));
  const [selected, setSelected] = useState(() => {
    const next = new Set<string>();
    for (const c of candidates) {
      if (c.selected_by_default) next.add(c.candidate_id);
    }
    return next;
  });
  const [dragId, setDragId] = useState<string | null>(null);
  const [reelName, setReelName] = useState(() => {
    const named = normalizeReelName(initialReelName);
    return isReelNameValid(named) ? named : '';
  });
  const [nameTouched, setNameTouched] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [playerArmed, setPlayerArmed] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const endSecRef = useRef(0);

  const byId = useMemo(() => {
    const map = new Map<string, CurationCandidate>();
    for (const c of candidates) map.set(c.candidate_id, c);
    return map;
  }, [candidates]);

  const ordered = order
    .map((id) => byId.get(id))
    .filter((c): c is CurationCandidate => Boolean(c));

  const selectedCount = ordered.filter((c) => selected.has(c.candidate_id)).length;
  const previewExpired = isPreviewExpired(sourcePreviewExpiresAt);
  const previewAvailable = Boolean(sourcePreviewUrl) && !previewExpired;
  const activePreview = previewId ? byId.get(previewId) ?? null : null;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const move = (id: string, dir: -1 | 1) => {
    setOrder((prev) => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
      return copy;
    });
  };

  const onDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    setOrder((prev) => {
      const without = prev.filter((id) => id !== dragId);
      const at = without.indexOf(targetId);
      if (at < 0) return prev;
      without.splice(at, 0, dragId);
      return without;
    });
    setDragId(null);
  };

  const nameValid = isReelNameValid(reelName);
  const canSubmit = selectedCount > 0 && nameValid && !submitting;

  const submit = () => {
    setNameTouched(true);
    const trimmed = normalizeReelName(reelName);
    if (!isReelNameValid(trimmed) || selectedCount === 0) return;
    const ids = ordered
      .filter((c) => selected.has(c.candidate_id))
      .map((c) => c.candidate_id);
    onSubmit(ids, trimmed);
  };

  const seekAndPlay = (startSec: number, endSec: number) => {
    const video = videoRef.current;
    if (!video) return;
    endSecRef.current = endSec;
    setBuffering(true);
    const playFrom = () => {
      try {
        video.currentTime = Math.max(0, startSec);
      } catch {
        // Ignore seek errors until metadata is ready.
      }
      void video.play().catch(() => {
        setBuffering(false);
      });
    };
    if (video.readyState >= 1) {
      playFrom();
    } else {
      const onMeta = () => {
        video.removeEventListener('loadedmetadata', onMeta);
        playFrom();
      };
      video.addEventListener('loadedmetadata', onMeta);
    }
  };

  const openPreview = (cand: CurationCandidate) => {
    if (!previewAvailable || !sourcePreviewUrl) return;
    if (!playerArmed) setPlayerArmed(true);
    setPreviewId(cand.candidate_id);
    // Seek after the video mounts under this row.
    requestAnimationFrame(() => {
      seekAndPlay(cand.start_sec, cand.end_sec);
    });
  };

  const replay = () => {
    if (!activePreview) return;
    seekAndPlay(activePreview.start_sec, activePreview.end_sec);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !previewId) return;

    const onTimeUpdate = () => {
      if (video.currentTime >= endSecRef.current - 0.05) {
        video.pause();
        try {
          video.currentTime = endSecRef.current;
        } catch {
          // ignore
        }
      }
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onSeeked = () => setBuffering(false);

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('seeked', onSeeked);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [previewId, playerArmed]);

  // If the URL rotates (refresh), re-point the armed player without autoplay.
  useEffect(() => {
    if (!playerArmed || !sourcePreviewUrl || !videoRef.current) return;
    if (videoRef.current.src !== sourcePreviewUrl) {
      videoRef.current.src = sourcePreviewUrl;
    }
  }, [sourcePreviewUrl, playerArmed]);

  return (
    <div className="space-y-6 py-8" data-testid="studio-curation">
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-semibold">Pick your plays</h1>
        <p className="text-sm text-[var(--studio-muted)]">
          Pre-checked plays match your team. Uncheck anything clipped or wrong-side,
          drag to reorder, then cut the reel.
        </p>
        {deadlineAt ? (
          <p className="text-xs text-[var(--studio-muted)]">
            Auto-cuts if you leave it — deadline {new Date(deadlineAt).toLocaleString()}
          </p>
        ) : null}
        {!previewAvailable ? (
          <div className="studio-curation-preview-unavailable" data-testid="studio-curation-preview-unavailable">
            <p className="text-xs text-[var(--studio-muted)]">
              Preview unavailable — refresh to mint a fresh source link.
            </p>
            {onRefreshPreview ? (
              <button
                type="button"
                className="studio-btn-ghost"
                disabled={refreshingPreview}
                onClick={() => {
                  void onRefreshPreview();
                }}
                data-testid="studio-curation-preview-refresh"
              >
                {refreshingPreview ? 'Refreshing…' : 'Refresh preview'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul className="space-y-3" data-testid="studio-curation-list">
        {ordered.map((cand, index) => {
          const checked = selected.has(cand.candidate_id);
          const shortClip = cand.duration_sec > 0 && cand.duration_sec < 4.5;
          const isPreviewing = previewId === cand.candidate_id;
          return (
            <li
              key={cand.candidate_id}
              className={`studio-curation-card ${checked ? 'is-selected' : ''}`}
              draggable
              onDragStart={() => setDragId(cand.candidate_id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(cand.candidate_id)}
              data-testid={`studio-curation-row-${cand.candidate_id}`}
            >
              <div className="studio-curation-card-main">
                <label className="studio-curation-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(cand.candidate_id)}
                    aria-label={`Include ${cand.label}`}
                  />
                </label>
                <div className="studio-curation-thumb" aria-hidden>
                  {cand.thumbnail_url ? (
                    <img src={cand.thumbnail_url} alt="" />
                  ) : (
                    <span>{formatDuration(cand.duration_sec)}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium truncate">{cand.label}</p>
                    {cand.team ? (
                      <span className="studio-curation-team">{cand.team}</span>
                    ) : null}
                    {shortClip ? (
                      <span className="studio-curation-warn">short clip</span>
                    ) : null}
                  </div>
                  {cand.why ? (
                    <p className="text-xs text-[var(--studio-muted)] line-clamp-2">
                      {cand.why}
                    </p>
                  ) : null}
                  <p className="text-xs text-[var(--studio-muted)]">
                    {formatDuration(cand.duration_sec)} · #{index + 1}
                  </p>
                  <div className="studio-curation-preview-actions">
                    <button
                      type="button"
                      className="studio-btn-ghost studio-curation-preview-btn"
                      disabled={!previewAvailable}
                      title={
                        previewAvailable
                          ? 'Preview this clip'
                          : 'Preview unavailable — refresh'
                      }
                      aria-label={`Preview ${cand.label}`}
                      data-testid={`studio-curation-preview-${cand.candidate_id}`}
                      onClick={() => openPreview(cand)}
                    >
                      ▶ Preview
                    </button>
                  </div>
                </div>
                <div className="studio-curation-reorder">
                  <button
                    type="button"
                    className="studio-btn-ghost"
                    aria-label="Move up"
                    onClick={() => move(cand.candidate_id, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="studio-btn-ghost"
                    aria-label="Move down"
                    onClick={() => move(cand.candidate_id, 1)}
                  >
                    ↓
                  </button>
                </div>
              </div>

              {isPreviewing && playerArmed && sourcePreviewUrl ? (
                <div
                  className="studio-curation-preview"
                  data-testid={`studio-curation-player-${cand.candidate_id}`}
                >
                  <video
                    ref={videoRef}
                    className="studio-curation-preview-video"
                    src={sourcePreviewUrl}
                    preload="metadata"
                    playsInline
                    controls={false}
                    data-testid="studio-curation-shared-video"
                  />
                  <div className="studio-curation-preview-bar">
                    <button
                      type="button"
                      className="studio-btn-ghost"
                      onClick={replay}
                      data-testid="studio-curation-replay"
                    >
                      Replay
                    </button>
                    <span className="text-xs text-[var(--studio-muted)]">
                      clip {index + 1} · {formatDuration(cand.duration_sec)}
                    </span>
                    {buffering ? (
                      <span
                        className="text-xs text-[var(--studio-accent)]"
                        data-testid="studio-curation-buffering"
                      >
                        Loading…
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {error ? (
        <p className="text-center text-sm text-[var(--studio-danger)]" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col items-center gap-3 w-full max-w-xl mx-auto">
        <ReelNameField
          value={reelName}
          onChange={setReelName}
          showError={nameTouched}
          disabled={submitting}
        />
        <button
          type="button"
          className="studio-btn-primary"
          disabled={!canSubmit}
          onClick={submit}
          data-testid="studio-curation-submit"
        >
          {submitting ? 'Cutting…' : `Cut my reel (${selectedCount})`}
        </button>
      </div>
    </div>
  );
};

export default CurationReviewPanel;
